import { useState, useEffect, useCallback } from "react";
import { connect } from "react-redux";
import ReactDOMServer from "react-dom/server";

import Modal from "../common_modal/Modal";

import { set_value_properties } from "../../App/actions/propertiesActions";
import { set_value_sini } from "../../App/actions/sini_v2";
import { set_value_layer } from "../../App/actions/layerActions";
import { get_sini_data } from "../../App/actions/get_sini_data";

import dict from "../../Data/dict.json";
import uuid from "../../App/validation/uuid";
import domain_list from "../../Data/domain_list";

import maplibregl from "maplibre-gl";

const pointer_id = "geojson_search";
const geometry_pointer_id = `${pointer_id}_preview`;
const geometry_line_id = `${pointer_id}_preview_line`;

// ─── HARDCODED 3D LAYER IDS ───────────────────────────────────────────────────
const HARDCODED_3D_LAYER_IDS = [
  "3d-station-lawang-sewu",
  "3d-station-monas",
  "3d-station-gedung-sate",
  "3d-station-gelora",
  "3d-station-tasikmalaya",
  "3d-station-masjid-raya-bandung",
  "3d_gereja_blenduk",
  "3d_istora_senayan",
  "3d_monumen_selamat_datang",
  "3d_spiegel",
  "3d_museum_mandala_bhakti",
  "3d_hotel_kotta",
  "3d_suro_boyo",
  "3d_pandanaran",
];
// ──────────────────────────────────────────────────────────────────────────────

const LAYER_SEARCH = ({
  properties,
  layer,
  set_value_properties,
  auth,
  get_sini_data,
  set_value_sini,
  set_value_layer,
}) => {
  const [modal_sini_trigger, set_modal_sini_trigger] = useState(false);

  // Extract specific properties to avoid hook dependency warnings & infinite loops
  const map_object = layer?.map_object;
  const search_geometry = properties?.search_geometry;
  const uuid_search = properties?.uuid_search;
  const basemap_used = properties?.basemap_used;

  const toggle_sini_trigger = useCallback(() => {
    set_modal_sini_trigger((prev) => !prev);
  }, []);

  const on_remove_marker = useCallback(() => {
    set_modal_sini_trigger(false);

    if (map_object) {
      if (map_object.getLayer(pointer_id)) map_object.removeLayer(pointer_id);
      if (map_object.getLayer(geometry_pointer_id))
        map_object.removeLayer(geometry_pointer_id);
      if (map_object.getLayer(geometry_line_id))
        map_object.removeLayer(geometry_line_id);

      if (map_object.getSource(pointer_id)) map_object.removeSource(pointer_id);
      if (map_object.getSource(geometry_pointer_id))
        map_object.removeSource(geometry_pointer_id);
      if (map_object.getSource(geometry_line_id))
        map_object.removeSource(geometry_line_id);
    }

    const marker_element = document.getElementById("search_map");
    if (marker_element) {
      marker_element.remove();
      set_value_properties({
        key: "search_geometry",
        value: null,
      });
      set_value_properties({
        key: "uuid_search",
        value: "",
      });
    }
  }, [map_object, set_value_properties]);

  const trigger_sini = useCallback(async () => {
    toggle_sini_trigger();
    set_value_properties({
      key: "sini_data_mode",
      value: "Site Analysis",
    });

    set_value_layer({
      key: "statistic_mode",
      value: "sini",
    });

    const { domain } = auth;
    const show_survey_1 = domain_list?.[domain]?.show_survey_1;
    const { type, coordinates, center } = search_geometry || {};
    let latitude = null;
    let longitude = null;

    if (type === "Point") {
      longitude = coordinates[0];
      latitude = coordinates[1];
    } else if (center) {
      longitude = +center.long;
      latitude = +center.lat;
    }

    const params = {
      request_id: uuid(),
      long: longitude,
      lat: latitude,
      km_rad: 1,
      sini_data_mode: "sini_general",
      show_survey_1,
    };

    on_remove_marker();

    await get_sini_data(params);

    set_value_sini({
      key: "sini_menu_active",
      value: "",
    });
    set_value_sini({
      key: "is_sini_visible",
      value: true,
    });
    set_value_layer({
      key: "statistic_mode",
      value: "sini",
    });
  }, [
    toggle_sini_trigger,
    set_value_properties,
    set_value_layer,
    auth,
    search_geometry,
    on_remove_marker,
    get_sini_data,
    set_value_sini,
  ]);

  // ─── AUTO-SORTER (Safeguard for when 3D models load AFTER Search) ──────────
  useEffect(() => {
    if (!map_object) return;

    const enforceLayerOrder = () => {
      if (!map_object.getStyle()) return;
      const layers = map_object.getStyle().layers;
      if (!layers) return;

      // Find all hardcoded 3D models currently active on the map
      const active3DIds = HARDCODED_3D_LAYER_IDS.filter((id) =>
        map_object.getLayer(id),
      );
      if (active3DIds.length === 0) return;

      // Find the lowest stack index among all loaded 3D models
      let lowest3DIndex = Infinity;
      let lowest3DId = null;
      active3DIds.forEach((id) => {
        const index = layers.findIndex((l) => l.id === id);
        if (index !== -1 && index < lowest3DIndex) {
          lowest3DIndex = index;
          lowest3DId = id;
        }
      });

      if (!lowest3DId) return;

      // Ensure search layers are always explicitly moved underneath the 3D models
      const searchLayers = [geometry_pointer_id, geometry_line_id, pointer_id];
      searchLayers.forEach((searchId) => {
        const searchIndex = layers.findIndex((l) => l.id === searchId);
        if (searchIndex !== -1 && searchIndex > lowest3DIndex) {
          map_object.moveLayer(searchId, lowest3DId);
        }
      });
    };

    // Run when style updates (like when a 3D model finishes loading)
    map_object.on("styledata", enforceLayerOrder);
    return () => {
      map_object.off("styledata", enforceLayerOrder);
    };
  }, [map_object]);

  const on_render = useCallback(() => {
    const language = localStorage?.language || "ina";

    if (!search_geometry || !map_object) return;

    const geometry = {
      type: search_geometry.type,
      coordinates: search_geometry.coordinates,
    };

    const { type, coordinates, center, name } = search_geometry || {};
    let latitude = null;
    let longitude = null;

    let layout = {};

    // ─── BULLETPROOF Z-INDEX FIX ────────────────────────────────────────────
    const activeLayers = map_object.getStyle().layers || [];

    const active3DId = HARDCODED_3D_LAYER_IDS.find((id) =>
      activeLayers.some((l) => l.id === id),
    );

    let beforeId = active3DId;
    if (!beforeId) {
      const firstSymbol = activeLayers.find((l) => l.type === "symbol");
      beforeId = firstSymbol ? firstSymbol.id : undefined;
    }
    // ────────────────────────────────────────────────────────────────────────

    if (type === "Point") {
      longitude = coordinates[0];
      latitude = coordinates[1];

      if (map_object?.getSource(geometry_pointer_id)) {
        map_object.removeSource(geometry_pointer_id);
      }
      if (map_object?.getLayer(geometry_pointer_id)) {
        map_object.removeLayer(geometry_pointer_id);
      }
      if (map_object?.getSource(geometry_line_id)) {
        map_object.removeSource(geometry_line_id);
      }
      if (map_object?.getLayer(geometry_line_id)) {
        map_object.removeLayer(geometry_line_id);
      }
    } else {
      longitude = center.long;
      latitude = center.lat;

      let geojson = {
        type: "FeatureCollection",
        features: [{ type: "Feature", properties: {}, geometry }],
      };

      let paint_type = "line";
      let paint = {
        "line-color": "#21a5f6",
        "line-width": 4,
      };

      layout = {
        "line-cap": "round",
        "line-join": "round",
      };

      if (type === "Polygon") {
        paint_type = "fill-extrusion";
        paint = {
          "fill-extrusion-color": "#a0d061",
          "fill-extrusion-opacity": 0.2,
          "fill-extrusion-height": 0,
        };
      }

      let body = {
        id: geometry_pointer_id,
        source: geometry_pointer_id,
        type: paint_type,
        paint,
      };

      if (!map_object?.getSource(geometry_pointer_id)) {
        map_object.addSource(geometry_pointer_id, {
          type: "geojson",
          data: geojson,
        });
      } else {
        map_object.getSource(geometry_pointer_id).setData(geojson);
      }

      const layer_existing = map_object?.getLayer(geometry_pointer_id);
      if (!layer_existing) {
        map_object.addLayer(body, beforeId);
      } else {
        if (layer_existing.type !== paint_type) {
          map_object.removeLayer(geometry_pointer_id);
          map_object.addLayer(body, beforeId);
        }
      }

      if (!map_object?.getSource(geometry_line_id)) {
        map_object.addSource(geometry_line_id, {
          type: "geojson",
          data: geojson,
        });
      } else {
        map_object.getSource(geometry_line_id).setData(geojson);
      }

      if (!map_object?.getLayer(geometry_line_id)) {
        map_object.addLayer(
          {
            id: geometry_line_id,
            source: geometry_line_id,
            type: "line",
            paint: { "line-color": "#21a5f6", "line-width": 4 },
            layout: layout,
          },
          beforeId,
        );
      }
    }

    if (map_object && longitude !== null && latitude !== null && type) {
      let marker_element = document.getElementById("search_map");
      if (!marker_element) {
        marker_element = document.createElement("div");
      }
      marker_element.id = "search_map";
      marker_element.style.position = "absolute";
      marker_element.style.transform = "translate(-50%, -100%)";

      const label_text = name || dict["Result"][language];

      const marker_jsx = (
        <div
          id="search_title"
          title={label_text}
          style={{
            position: "relative",
            paddingBottom: "12px",
            cursor: "pointer",
            pointerEvents: "auto",
          }}
        >
          {/* Outer bubble wrapper: Made relative to position the absolute close button */}
          <div
            style={{
              position: "relative",
              background: "#ffffff",
              borderRadius: "16px",
              boxShadow:
                "0 10px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1)",
              border: "1px solid rgba(0, 0, 0, 0.04)",
              padding: "8px 14px",
              maxWidth: "180px",
              textAlign: "center",
            }}
          >
            {/* Close Button UI (React onClick stripped, bound natively below) */}
            <div
              id="search_close_btn"
              title="Close"
              style={{
                position: "absolute",
                top: "-6px",
                right: "-6px",
                width: "20px",
                height: "20px",
                borderRadius: "50%",
                backgroundColor: "#ffffff",
                border: "1px solid rgba(0, 0, 0, 0.08)",
                boxShadow: "0 2px 4px rgba(0, 0, 0, 0.1)",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                fontSize: "10px",
                fontWeight: "bold",
                color: "#666",
                cursor: "pointer",
                zIndex: 10,
              }}
            >
              ✕
            </div>

            {/* Inner text constraint wrapper (Prevents 3rd line completely) */}
            <div
              style={{
                display: "-webkit-box",
                WebkitLineClamp: 2,
                WebkitBoxOrient: "vertical",
                overflow: "hidden",
                textOverflow: "ellipsis",
                fontWeight: "600",
                fontSize: "14px",
                lineHeight: "18px",
                maxHeight: "36px",
                color: "#111111",
              }}
            >
              {label_text}
            </div>
          </div>

          <div
            style={{
              position: "absolute",
              bottom: "0px",
              left: "50%",
              transform: "translateX(-50%)",
              width: 0,
              height: 0,
              borderLeft: "10px solid transparent",
              borderRight: "10px solid transparent",
              borderTop: "12px solid #ffffff",
              filter: "drop-shadow(0 3px 2px rgba(0, 0, 0, 0.08))",
              pointerEvents: "none",
            }}
          />
        </div>
      );

      const marker_html = ReactDOMServer.renderToString(marker_jsx);
      marker_element.innerHTML = marker_html;
      const lngLat = new maplibregl.LngLat(longitude, latitude);
      const point = map_object.project(lngLat);
      marker_element.style.left = `${point.x}px`;
      marker_element.style.top = `${point.y}px`;
      map_object.getContainer().appendChild(marker_element);

      // --- NATIVE EVENT LISTENER BINDINGS ---
      const search_title = document.getElementById("search_title");
      if (search_title) {
        search_title.addEventListener("click", toggle_sini_trigger);
      }

      const search_close = document.getElementById("search_close_btn");
      if (search_close) {
        search_close.addEventListener("click", (e) => {
          e.stopPropagation(); // Prevents click from bubbling to search_title
          on_remove_marker();
        });
      }

      map_object.on("move", () => {
        const newPoint = map_object.project(lngLat);
        marker_element.style.left = `${newPoint.x}px`;
        marker_element.style.top = `${newPoint.y}px`;
      });

      const geojson_search = {
        type: "FeatureCollection",
        features: [
          {
            type: "Feature",
            geometry: { type: "Point", coordinates: [longitude, latitude] },
            properties: {},
          },
        ],
      };
      if (!map_object.getSource("geojson_search")) {
        map_object.addSource("geojson_search", {
          type: "geojson",
          data: geojson_search,
        });
      } else {
        map_object.getSource("geojson_search").setData(geojson_search);
      }
      if (!map_object.getLayer("geojson_search")) {
        map_object.addLayer(
          {
            id: "geojson_search",
            source: "geojson_search",
            type: "circle",
            paint: {
              "circle-radius": 5,
              "circle-color": "#000",
              "circle-stroke-width": 2,
              "circle-stroke-color": "#ffffff",
            },
            // FIX: Removed `...layout` which was improperly spreading line-specific
            // properties ('line-cap', 'line-join') into a circle layer.
            layout: { visibility: "visible" },
          },
          beforeId,
        );
      }
    }
  }, [map_object, search_geometry, toggle_sini_trigger, on_remove_marker]);

  useEffect(() => {
    if (map_object) {
      if (uuid_search) {
        on_render();
      }
    }
  }, [map_object, uuid_search, basemap_used, on_render]);

  const language = localStorage?.language || auth.language;
  const { isAuthenticated } = auth;

  // Extracting the full text specifically for the Modal
  const modal_result_text =
    search_geometry?.name || dict?.["Result"]?.[language] || "Result";

  const modal_sini_trigger_content = modal_sini_trigger && (
    <Modal
      modalSize="small"
      isOpen={modal_sini_trigger}
      onClose={toggle_sini_trigger}
      title="Result detail"
    >
      <div className="box-body" id="box-body">
        <p className="container background_grey_light text_medium margin_bottom_extra">
          {modal_result_text}
        </p>
        <br />
        <section className="margin_top">
          {isAuthenticated && (
            <>
              <button
                onClick={trigger_sini}
                className="button button_white margin_bottom outline_black"
              >
                {dict?.["Try SINI at your location"]?.[language]}
              </button>
              <br />
            </>
          )}
          <button
            className="button background_red margin_bottom"
            onClick={on_remove_marker}
          >
            Remove marker
          </button>
        </section>
      </div>
    </Modal>
  );

  return <main>{modal_sini_trigger_content}</main>;
};

const mapStateToProps = (state) => ({
  auth: state.auth,
  layer: state.layer,
  properties: state.properties,
});

export default connect(mapStateToProps, {
  get_sini_data,
  set_value_sini,
  set_value_layer,
  set_value_properties,
})(LAYER_SEARCH);
